import {
  Baseline,
  OneOfListElements,
  OneOfSelectableElements,
  Project,
} from '@shared/models/project.model';
import { pick } from '@shared/helpers/object.helpers';
import { useMemoDeepEqual } from '@client/hooks/use-utils.hooks';
import { useProjectStateStore } from '../project/project.store';
import useUIStore from './ui.store';
import { IUIState } from './ui.model';
import { useEffect } from 'react';
import {
  cacheFactory,
  cacheFactoryReset,
  required,
} from '@shared/helpers/function.helpers';
import {
  getAllBuildingVersions,
  getBuildingVersionById,
  getElementById,
  isBuildingVersionElement,
} from '@shared/helpers/recursive-element.helpers';
import { IElementProperty } from '@shared/models/element-property.model';
import { OptionallyRequired } from '@shared/models/type-helpers.model';
import { isProjectInfoOrFolder } from '@shared/helpers/project-folder.helpers';
import { getProject } from '../project';
import { useProductsLookup } from '../product/product.hook';

/**
 * Get store state without rerendering
 */
export const getUIState = useUIStore.getState;

const selectedVersionSelector = (
  selectedVersionId: IUIState['selectedVersionId'],
  project: Project,
): Baseline | undefined => {
  return getBuildingVersionById(project, selectedVersionId);
};

const selectedElementSelector = (
  selectedElementId: IUIState['selectedElementId'],
  version: Baseline,
): OneOfSelectableElements | undefined => {
  if (selectedElementId === undefined) return undefined;
  return getElementById(version, selectedElementId);
};

const selectedElementIdSelector = (
  state: IUIState,
): OneOfSelectableElements['id'] | undefined => state.selectedElementId;

/** Walks the project tree — call only when a required version is actually missing. */
const missingVersionMessage = (
  project: Project,
  selectedVersionId: IUIState['selectedVersionId'],
): string =>
  `Selected version (${selectedVersionId}) not found in project ${project.id}. Available versions: ${getAllBuildingVersions(
    project,
  )
    .map((version) => version.id)
    .join(', ')}`;

/**
 * Get selected version without causing rerenders.
 * @param require Pass true to throw an error if no version is selected
 * @returns
 */
export const getSelectedVersion = <
  T extends boolean = false,
  R = OptionallyRequired<Baseline, T>,
>(
  require: T = false as T,
): R => {
  const project = getProject();
  const uiState = getUIState();

  const selectedVersion = selectedVersionSelector(
    uiState.selectedVersionId,
    project,
  );
  // this is an ugly hack. If the selected version is deleted we fallback to the active version. The next pass of routing params validation will
  // detect that the selected version doesn't exist and update the url params to the active version. It's ugly af that it's disconnected like this.
  const activeVersion = selectedVersionSelector(
    project.active_version_id,
    project,
  );
  const version = selectedVersion ?? activeVersion;
  return required(
    version,
    require && !version
      ? missingVersionMessage(project, uiState.selectedVersionId)
      : require,
  );
};

/**
 * Get selected element id without causing rerenders
 * @returns
 */
export const getSelectedElementId = ():
  OneOfSelectableElements['id'] | undefined =>
  getSelectedVersion() ? getUIState().selectedElementId : undefined;

export const getSelectedElement = (): OneOfSelectableElements | undefined => {
  const version = getSelectedVersion();
  const selectedElementId = getSelectedElementId();
  if (!version || !selectedElementId) return undefined;
  return selectedElementSelector(selectedElementId, version);
};

/**
 * Get current selected version
 * @returns
 */
export const useSelectedVersion = (): Baseline | undefined => {
  const selectedVersionId = useSelectedVersionId();
  // Use store selector to only trigger rerenders when selected version changes
  return useProjectStateStore((state) =>
    selectedVersionSelector(selectedVersionId, state.project),
  );
};

const selectedVersionIdSelector = (
  state: IUIState,
): Baseline['id'] | undefined => state.selectedVersionId;

export const getSelectedVersionId = (): Baseline['id'] | undefined =>
  selectedVersionIdSelector(getUIState());

/**
 * Get current selected version id
 * To trigger only when selectedVersion id changes
 * @returns
 */
export const useSelectedVersionId = (): Baseline['id'] | undefined =>
  useUIStore(selectedVersionIdSelector);

/**
 * Get products available on version.
 * Will trigger only when selectedVersion.products changes
 * @returns
 */
export const useSelectedVersionProducts = ():
  Baseline['products'] | undefined => {
  const selectedVersionId = useSelectedVersionId();
  // Use store selector to only trigger rerenders when selected version products changes
  return useProjectStateStore(
    (state) =>
      selectedVersionSelector(selectedVersionId, state.project)?.products,
  );
};

/**
 * Get products in version combined with products from store for maximum flexibility.
 * Cached globally rather than per component instance: list rows call this per row, and a
 * `useMemo` would rebuild the merged record once for every mounted row.
 * @returns Products record with products from store and version
 */
export const useSelectedVersionProductsAndStoreProducts =
  (): Baseline['products'] => {
    const versionProducts = useSelectedVersionProducts();
    const productsLookup = useProductsLookup();
    return cacheFactory(
      () => ({ ...productsLookup, ...versionProducts }),
      'useSelectedVersionProductsAndStoreProducts',
      [versionProducts, productsLookup],
    );
  };

export const useSelectedElementId = ():
  OneOfSelectableElements['id'] | undefined => {
  return useUIStore(selectedElementIdSelector);
};

export const useSelectedElement = (): OneOfSelectableElements | undefined => {
  const version = useSelectedVersion();
  const selectedElementId = useSelectedElementId();
  if (!version || !selectedElementId) return undefined;
  return selectedElementSelector(selectedElementId, version);
};

/**
 * Detect if an element is selected. Versions will be selected if they are the selected version.
 * @param element
 * @returns
 */
export const useIsSelected = (element: OneOfListElements): boolean => {
  const { selectedProjectFolderId } = useUIState('selectedProjectFolderId');
  const selectedElementId = useSelectedElementId();
  const selectedVersionId = useSelectedVersionId();

  if (isProjectInfoOrFolder(element)) {
    return selectedProjectFolderId === element.id;
  }
  if (isBuildingVersionElement(element)) {
    return selectedVersionId === element.id;
  }

  return selectedElementId === element.id;
};

type UseIsAddedPropertyOptions = {
  /** If true, the property (if the provided property was the added property) will be cleared when the component unmounts */
  autoClearOnUnmount?: boolean;
};
export const useIsAddedProperty = (
  /** The property to check if it is the added property */
  property: IElementProperty,
  { autoClearOnUnmount }: UseIsAddedPropertyOptions = {
    autoClearOnUnmount: false,
  },
): boolean => {
  const currentAddedPropertyId = useUIStore((state) => state.addedPropertyId);
  const isMatching = property.id === currentAddedPropertyId;

  useEffect(() => {
    if (!autoClearOnUnmount || !isMatching) return; // don't do autoclearing
    return () => useUIStore.getState().clearSelection('addedPropertyId'); // cleanup function clears the added property id
  }, [isMatching, autoClearOnUnmount]);

  return isMatching;
};
/**
 * Subscribe to a subset of {@link IUIState}, rerendering only when one of the keys changes.
 * @param keys Keys to subscribe to. Pass none to subscribe to the whole state.
 * @returns The picked slice of the store
 */
export function useUIState<K extends keyof IUIState>(
  ...keys: K[]
): Pick<IUIState, K> {
  // Keep the selector stable across renders so the store subscription isn't recreated
  const selector = useMemoDeepEqual(
    () => (o: IUIState) => (keys.length ? pick(o, ...keys) : o),
    keys,
  );
  return useUIStore(selector);
}

/**
 * Subscribe to project state changes and unsubscribe when not used anymore
 */
export const useInitUIStore = (): void => {
  useEffect(() => {
    // When project updates make sure selected version is
    const projectUnsubscribeFn = useProjectStateStore.subscribe(
      ({ project }, { project: prevProject }) => {
        if (project !== prevProject) {
          const uiState = getUIState();
          const projectId = project?.id;
          const prevId = prevProject?.id;
          const versionId = uiState.selectedVersionId;
          const versionIdIsRemoved =
            projectId !== 0 &&
            getAllBuildingVersions(project).every(
              (version) => version.id !== versionId,
            );

          // make sure we're not just setting project the first time (moving from projectId 0)
          const projectHasChanged = projectId !== prevId && prevId !== 0;
          if (projectHasChanged || versionIdIsRemoved) {
            // Clear added-property highlight; keep addedElementId so a new row can still highlight
            uiState.clearSelections('addedElementId');
            useUIStore.setState({ observedElementIds: {} });

            // Clear caches to not store old data
            cacheFactoryReset();
          }
        }
      },
    );

    // Unusbscribe when not used anymore
    return projectUnsubscribeFn;
  }, []);
};
